import { useEffect, useRef } from "react";
import { notifyError } from "common/utils/app-monitoring/notify";
import { loadPackage } from "common/utils/package-loader";

export type FlickityOptions = {
  cellAlign?: "left" | "center" | "right";
  contain?: boolean;
  prevNextButtons?: boolean;
  wrapAround?: boolean;
  pageDots?: boolean;
  draggable?: boolean;
  freeScroll?: boolean;
  groupCells?: boolean | number | string;
  lazyLoad?: boolean | number;
  percentPosition?: boolean;
  setGallerySize?: boolean;
  adaptiveHeight?: boolean;
  watchCSS?: boolean;
  dragThreshold?: number;
  selectedAttraction?: number;
  friction?: number;
};

export type FlickityInstance = {
  next: () => void;
  previous: () => void;
  select: (index: number) => void;
  resize: () => void;
  destroy: () => void;
  on: (eventName: string, callback: (evt: Event) => void) => void;
  off: (eventName: string, callback: (evt: Event) => void) => void;
};

export type UseFlickityProps = {
  options?: FlickityOptions;
  enabled?: boolean;
};

export type UseFlickityReturn = {
  carouselRef: React.RefObject<HTMLDivElement | null>;
  flickity: FlickityInstance | null;
  next: () => void;
  previous: () => void;
  select: (index: number) => void;
  resize: () => void;
};

const DEFAULT_OPTIONS: FlickityOptions = {
  cellAlign: "center",
  contain: true,
  prevNextButtons: false,
  wrapAround: true,
  pageDots: false
};

export function useFlickity({
  options = {} as FlickityOptions,
  enabled = true
}: UseFlickityProps = {}): UseFlickityReturn {
  const carouselRef = useRef<HTMLDivElement>(null);
  const flickityInstanceRef = useRef<FlickityInstance | null>(null);

  useEffect(() => {
    if (!enabled || !carouselRef.current) return;

    const initFlickity = async () => {
      try {
        const FlickityConstructor = await loadPackage("flickity-as-nav-for");

        if (!carouselRef.current || flickityInstanceRef.current) return;

        flickityInstanceRef.current = new FlickityConstructor(carouselRef.current, {
          ...DEFAULT_OPTIONS,
          ...options
        }) as FlickityInstance;

        flickityInstanceRef.current.on("dragStart", (evt: Event) => {
          const target = evt.target as HTMLElement;
          const parent = target.parentNode as HTMLElement | null;
          parent?.classList.add("no-pointer");
        });

        flickityInstanceRef.current.on("dragEnd", (evt: Event) => {
          const target = evt.target as HTMLElement;
          const parent = target.parentNode as HTMLElement | null;
          parent?.classList.remove("no-pointer");
        });
      } catch (error) {
        notifyError(error instanceof Error ? error : new Error(String(error)));
      }
    };

    initFlickity();
  }, [enabled, options]);

  useEffect(
    () => () => {
      if (flickityInstanceRef.current) {
        flickityInstanceRef.current.destroy();
        flickityInstanceRef.current = null;
      }
    },
    []
  );

  return {
    carouselRef,
    flickity: flickityInstanceRef.current,
    next: () => flickityInstanceRef.current?.next(),
    previous: () => flickityInstanceRef.current?.previous(),
    select: (index: number) => flickityInstanceRef.current?.select(index),
    resize: () => flickityInstanceRef.current?.resize()
  };
}
